<template>
  <Badge
    :icon-before="priorityIcon"
    :priority="priorityName"
  >
    {{ priorityLabel }}
  </Badge>
</template>

<script setup>
import { computed } from 'vue';
import { Badge } from '@stakeui';
import { capitalize } from '@frontend/shared/Util.js';

const props = defineProps({
  priority: {
    type: [Number, String],
    required: true,
  },
});

const PRIORITY_NAMES = {
  1: 'critical',
  2: 'high',
  3: 'medium',
  4: 'low',
};

const priorityName = computed(() => {
  // Convert string to number if needed
  const priorityNumber = typeof props.priority === 'string' ? parseInt(props.priority, 10) : props.priority;

  if (typeof priorityNumber === 'number' && !isNaN(priorityNumber)) {
    return PRIORITY_NAMES[priorityNumber] || 'medium';
  }
  return 'medium';
});

const PRIORITY_ICONS = {
  critical: 'fas fa-exclamation-triangle',
  high: 'fas fa-exclamation-circle',
  medium: 'fas fa-circle',
  low: 'far fa-circle',
};

const priorityIcon = computed(() => PRIORITY_ICONS[priorityName.value] || 'far fa-circle');
const priorityLabel = computed(() => capitalize(priorityName.value));
</script>

<style scoped>
.priority-badge {
  display: inline-flex;
  vertical-align: top;
  flex-shrink: 0;
  border-radius: 0.5em;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;
  gap: 0.5em;
  padding: 0 0.5em;

  &.is-critical {
    background-color: var(--stk-danger-50);
    color: var(--stk-danger-90);
  }

  &.is-high {
    background-color: var(--stk-warning-90);
    color: var(--stk-warning-60);
  }

  &.is-medium {
    background-color: var(--stk-info-90);
    color: var(--stk-info-60);
  }

  &.is-low {
    background-color: var(--stk-success-90);
    color: var(--stk-success-50);
  }
}
</style>
